/**
 * O que faz: organiza a agenda operacional de retornos por atraso, hoje, próximos dias e lista completa.
 * Onde usa: ReturnsPage na rota autenticada /retornos.
 * Conexões: hooks de retornos, responsáveis, etapas, configuração de Kanban, autenticação e ReturnScheduleDialog.
 * Tabelas: lead_returns e return_kanban_settings via hooks/RPCs; não faz escrita direta no banco.
 * Data: 24/08/2026
 */

import { useMemo, useState } from 'react';
import { CalendarCheck2, CalendarPlus, Link2, Loader2, RefreshCw, SearchX, Unlink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useEtapasKanban } from '@/hooks/useEtapasKanban';
import { cn } from '@/lib/utils';
import { convertSaoPauloToUTC, getTodayInSaoPaulo } from '@/utils/timezoneUtils';
import { groupReturnsByDay } from './returnGrouping';
import { ReturnScheduleDialog } from './ReturnScheduleDialog';
import { ReturnTaskCard } from './ReturnTaskCard';
import type { LeadReturn, ReturnExecutorType } from './returnTypes';
import { useReturnsRange, useTransitionLeadReturn } from './useLeadReturns';
import { useReturnAssignees } from './useReturnAssignees';
import { useConfigureReturnKanbanStage, useReturnKanbanSettings } from './useReturnKanbanSettings';

type ReturnTab = 'overdue' | 'today' | 'nextSevenDays' | 'all';
type OwnershipFilter = 'mine' | 'all';
type ExecutorFilter = 'all' | ReturnExecutorType;

const ACTIVE_VISIBLE_STATUSES = new Set<LeadReturn['status']>([
  'pending',
  'claimed',
  'awaiting_approval',
  'failed',
]);

const EMPTY_RESPONSIBLE_FILTER = 'all';
const NO_KANBAN_STAGE_VALUE = '__sem_coluna_vinculada__';
const RANGE_DAYS_BEFORE = 30;
const RANGE_DAYS_AFTER = 30;

const emptyStateByTab: Record<ReturnTab, { title: string; description: string }> = {
  overdue: {
    title: 'Nenhum retorno atrasado',
    description: 'Quando algo passar do horário previsto, ele aparece aqui para priorização.',
  },
  today: {
    title: 'Nenhum retorno para hoje',
    description: 'Os contatos do dia aparecem nesta aba assim que forem programados.',
  },
  nextSevenDays: {
    title: 'Nenhum retorno nos próximos 7 dias',
    description: 'Use esta visão para antecipar a carga da equipe na semana.',
  },
  all: {
    title: 'Nenhum retorno encontrado',
    description: 'Ajuste os filtros ou agende um retorno a partir de uma conversa.',
  },
};

const addDaysToCalendarKey = (calendarKey: string, days: number) => {
  // Motivo: o filtro trabalha com dia civil; somar em UTC evita variações do fuso do navegador.
  const [year, month, day] = calendarKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);

  return date.toISOString().slice(0, 10);
};

const formatDateLabel = (calendarKey: string) => {
  // Motivo: manter o período legível para usuário final sem alterar o valor técnico do input.
  const [year, month, day] = calendarKey.split('-').map(Number);

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
};

const buildNoonReferenceDate = (calendarKey: string) => {
  // Motivo: usar meio-dia de São Paulo evita bordas de meia-noite ao agrupar por dia civil.
  return new Date(convertSaoPauloToUTC(`${calendarKey}T12:00`));
};

const buildRange = (calendarKey: string) => {
  const startDay = addDaysToCalendarKey(calendarKey, -RANGE_DAYS_BEFORE);
  const endDay = addDaysToCalendarKey(calendarKey, RANGE_DAYS_AFTER);

  return {
    startDay,
    endDay,
    startUtc: convertSaoPauloToUTC(`${startDay}T00:00`),
    endUtc: convertSaoPauloToUTC(`${endDay}T23:59`),
  };
};

const filterActiveReturns = (items: LeadReturn[]) => {
  // Motivo: a sessão humana só deve listar tarefas ainda pendentes de ação ou revisão.
  return items.filter(item => ACTIVE_VISIBLE_STATUSES.has(item.status));
};

const getTabItems = (tab: ReturnTab, grouped: ReturnType<typeof groupReturnsByDay>, allItems: LeadReturn[]) => {
  if (tab === 'all') {
    return allItems;
  }

  return grouped[tab];
};

export function ReturnsBoard() {
  const navigate = useNavigate();
  const { userProfile } = useAuthUser();
  const [selectedDate, setSelectedDate] = useState(() => getTodayInSaoPaulo());
  const [selectedTab, setSelectedTab] = useState<ReturnTab>('today');
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>('all');
  const [executorFilter, setExecutorFilter] = useState<ExecutorFilter>('all');
  const [responsibleFilter, setResponsibleFilter] = useState(EMPTY_RESPONSIBLE_FILTER);
  const [returnToReschedule, setReturnToReschedule] = useState<LeadReturn | null>(null);

  const range = useMemo(() => buildRange(selectedDate), [selectedDate]);
  const returnsQuery = useReturnsRange(range.startUtc, range.endUtc);
  const { data: assignees = [], isLoading: isLoadingAssignees } = useReturnAssignees();
  const transitionLeadReturn = useTransitionLeadReturn();
  const { data: etapasKanban = [], isLoading: isLoadingEtapasKanban } = useEtapasKanban();
  const returnKanbanSettings = useReturnKanbanSettings();
  const configureReturnKanbanStage = useConfigureReturnKanbanStage();

  const linkedKanbanStage = useMemo(
    () => etapasKanban.find(etapa => etapa.id === returnKanbanSettings.data?.etapa_kanban_id) ?? null,
    [etapasKanban, returnKanbanSettings.data?.etapa_kanban_id],
  );
  // Motivo: a configuração pertence ao administrador global ou ao representante da clínica, nunca ao operador comum da agenda.
  const canConfigureKanbanLink = userProfile?.profile_type === 'admin' || userProfile?.profile_type === 'clinica';

  const filteredReturns = useMemo(() => {
    const activeItems = filterActiveReturns(returnsQuery.data ?? []);

    // Motivo: filtros são aplicados antes do agrupamento para os contadores refletirem exatamente a visão atual.
    return activeItems.filter(item => {
      if (ownershipFilter === 'mine' && item.responsible_profile_id !== userProfile?.id) {
        return false;
      }

      if (executorFilter !== 'all' && item.executor_type !== executorFilter) {
        return false;
      }

      if (responsibleFilter !== EMPTY_RESPONSIBLE_FILTER && item.responsible_profile_id !== responsibleFilter) {
        return false;
      }

      return true;
    });
  }, [executorFilter, ownershipFilter, responsibleFilter, returnsQuery.data, userProfile?.id]);

  const groupedReturns = useMemo(
    () => groupReturnsByDay(filteredReturns, buildNoonReferenceDate(selectedDate)),
    [filteredReturns, selectedDate],
  );

  const tabItems = useMemo(
    () => getTabItems(selectedTab, groupedReturns, filteredReturns),
    [filteredReturns, groupedReturns, selectedTab],
  );

  const tabs = [
    { value: 'overdue' as const, label: 'Atrasados', count: groupedReturns.overdue.length },
    { value: 'today' as const, label: 'Hoje', count: groupedReturns.today.length },
    { value: 'nextSevenDays' as const, label: 'Próximos 7 dias', count: groupedReturns.nextSevenDays.length },
    { value: 'all' as const, label: 'Todos', count: filteredReturns.length },
  ];

  const handleOpenConversation = (item: LeadReturn) => {
    const params = new URLSearchParams();
    params.set('leadId', item.lead_id);
    navigate(`/chat?${params.toString()}`);
  };

  const handleTransition = async (item: LeadReturn, targetStatus: 'completed' | 'cancelled') => {
    // Motivo: todas as mudanças humanas passam pela RPC central para preservar auditoria e trava expected_updated_at.
    await transitionLeadReturn.mutateAsync({
      return_id: item.id,
      target_status: targetStatus,
      expected_updated_at: item.updated_at,
    });
  };

  const handleKanbanStageChange = (value: string) => {
    // Validação de permissão: o seletor só é exibido para admin, mas o banco repete a regra para impedir alteração por chamadas manuais.
    // Consequência: usuários da equipe consultam o vínculo, sem poder alterar a automação comercial da clínica.
    // Regra de negócio: uma única coluna por clínica é a porta de entrada do Kanban para a agenda de retornos.
    if (!canConfigureKanbanLink) {
      return;
    }

    configureReturnKanbanStage.mutate(
      value === NO_KANBAN_STAGE_VALUE ? null : value,
    );
  };

  const isLoading = returnsQuery.isLoading || isLoadingAssignees;
  const emptyState = emptyStateByTab[selectedTab];

  return (
    <>
      <section className="flex h-full min-h-0 flex-col bg-surface">
        <div className="flex flex-col gap-4 border-b border-gray-100 bg-white px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <CalendarCheck2 className="h-4 w-4" aria-hidden="true" />
              </span>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-gray-950">Retornos</h1>
                <p className="text-sm text-gray-500">
                  Período carregado: {formatDateLabel(range.startDay)} a {formatDateLabel(range.endDay)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => returnsQuery.refetch()}
              disabled={returnsQuery.isFetching}
              className="h-10 rounded-xl border-gray-200"
            >
              {returnsQuery.isFetching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Atualizar
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/chat')}
              className="h-10 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <CalendarPlus className="mr-2 h-4 w-4" aria-hidden="true" />
              Agendar retorno
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-3 border-b border-gray-100 bg-white px-1 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700">
                <Link2 className="h-4 w-4" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-gray-900">Coluna de retorno no Kanban</h2>
                <p className="mt-0.5 text-sm text-gray-500">
                  {returnKanbanSettings.isLoading
                    ? 'Carregando a integração com o Kanban...'
                    : linkedKanbanStage
                      ? `Ao arrastar um card para “${linkedKanbanStage.nome}”, o CRM pedirá data e motivo antes de mover.`
                      : 'Nenhuma coluna vinculada. Escolha uma para exigir agendamento ao receber um card.'}
                </p>
                {returnKanbanSettings.error ? (
                  <p className="mt-1 text-sm font-medium text-red-700">
                    Não foi possível carregar a integração com o Kanban. Atualize a página antes de mover cards.
                  </p>
                ) : null}
              </div>
            </div>

            {canConfigureKanbanLink ? (
              <label className="w-full shrink-0 sm:w-72">
                <span className="sr-only">Coluna vinculada ao retorno</span>
                <Select
                  value={returnKanbanSettings.data?.etapa_kanban_id ?? NO_KANBAN_STAGE_VALUE}
                  onValueChange={handleKanbanStageChange}
                  disabled={isLoadingEtapasKanban || configureReturnKanbanStage.isPending || returnKanbanSettings.isLoading}
                >
                  <SelectTrigger className="h-10 rounded-xl border-gray-200 bg-white">
                    {configureReturnKanbanStage.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : linkedKanbanStage ? (
                      <Link2 className="mr-2 h-4 w-4 text-sky-700" aria-hidden="true" />
                    ) : (
                      <Unlink className="mr-2 h-4 w-4 text-gray-500" aria-hidden="true" />
                    )}
                    <SelectValue placeholder="Escolher coluna" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_KANBAN_STAGE_VALUE}>Nenhuma coluna vinculada</SelectItem>
                    {etapasKanban.map(etapa => (
                      <SelectItem key={etapa.id} value={etapa.id}>
                        {etapa.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            ) : null}
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
            <div className="grid gap-3 lg:grid-cols-[minmax(180px,220px)_minmax(210px,1fr)_minmax(180px,220px)_minmax(180px,240px)]">
              <label className="space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Data</span>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(event) => {
                    // Validação de estabilidade: o input pode emitir vazio enquanto o usuário edita; manter a última data válida evita quebrar a query.
                    if (event.target.value) {
                      setSelectedDate(event.target.value);
                    }
                  }}
                  className="h-10 rounded-xl border-gray-200"
                />
              </label>

              <div className="space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Equipe</span>
                <div className="grid grid-cols-2 rounded-xl bg-gray-100 p-1">
                  {([
                    { value: 'mine' as const, label: 'Meus retornos' },
                    { value: 'all' as const, label: 'Toda a equipe' },
                  ]).map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setOwnershipFilter(option.value)}
                      className={cn(
                        'h-8 rounded-lg px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-200',
                        ownershipFilter === option.value
                          ? 'bg-white text-gray-950 shadow-sm'
                          : 'text-gray-500 hover:text-gray-800',
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Executor</span>
                <Select value={executorFilter} onValueChange={(value) => setExecutorFilter(value as ExecutorFilter)}>
                  <SelectTrigger className="h-10 rounded-xl border-gray-200">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="human">Humano</SelectItem>
                    <SelectItem value="internal_ai">IA do sistema</SelectItem>
                    <SelectItem value="external_ai">IA externa</SelectItem>
                  </SelectContent>
                </Select>
              </label>

              <label className="space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Responsável</span>
                <Select value={responsibleFilter} onValueChange={setResponsibleFilter} disabled={isLoadingAssignees}>
                  <SelectTrigger className="h-10 rounded-xl border-gray-200">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EMPTY_RESPONSIBLE_FILTER}>Todos</SelectItem>
                    {assignees.map(assignee => (
                      <SelectItem key={assignee.id} value={assignee.id}>
                        {assignee.nome_completo || 'Usuário sem nome'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            </div>
          </div>

          {returnsQuery.error ? (
            <div className="rounded-2xl border border-red-100 bg-red-50 p-5 text-red-800">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-base font-semibold">Não foi possível carregar os retornos</h2>
                  <p className="mt-1 text-sm text-red-700">Tente novamente para buscar a agenda mais recente da clínica.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => returnsQuery.refetch()}
                  className="h-10 rounded-xl border-red-200 bg-white text-red-700 hover:bg-red-50"
                >
                  Tentar novamente
                </Button>
              </div>
            </div>
          ) : (
            <Tabs value={selectedTab} onValueChange={(value) => setSelectedTab(value as ReturnTab)} className="min-h-0 flex-1">
              <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-2xl bg-white p-1 shadow-sm">
                {tabs.map(tab => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="h-9 shrink-0 rounded-xl px-3 text-sm data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-800 data-[state=active]:shadow-none"
                  >
                    {tab.label} {tab.count}
                  </TabsTrigger>
                ))}
              </TabsList>

              {tabs.map(tab => (
                <TabsContent key={tab.value} value={tab.value} className="mt-4">
                  {isLoading ? (
                    <div className="space-y-3">
                      {[0, 1, 2].map(item => (
                        <Skeleton key={item} className="h-36 rounded-2xl" />
                      ))}
                    </div>
                  ) : tabItems.length > 0 ? (
                    <div className="space-y-3">
                      {tabItems.map(item => (
                        <ReturnTaskCard
                          key={item.id}
                          item={item}
                          onOpenConversation={handleOpenConversation}
                          onComplete={(returnItem) => handleTransition(returnItem, 'completed')}
                          onReschedule={setReturnToReschedule}
                          onCancel={(returnItem) => handleTransition(returnItem, 'cancelled')}
                          isMutating={transitionLeadReturn.isPending}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex min-h-[260px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center">
                      <SearchX className="h-10 w-10 text-gray-300" aria-hidden="true" />
                      <h2 className="mt-3 text-lg font-semibold text-gray-900">{emptyState.title}</h2>
                      <p className="mt-1 max-w-md text-sm text-gray-500">{emptyState.description}</p>
                      <Badge variant="outline" className="mt-4 rounded-full border-gray-200 text-gray-500">
                        {formatDateLabel(selectedDate)}
                      </Badge>
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </section>

      {returnToReschedule ? (
        <ReturnScheduleDialog
          leadId={returnToReschedule.lead_id}
          leadName={returnToReschedule.lead_name}
          open={Boolean(returnToReschedule)}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setReturnToReschedule(null);
            }
          }}
          initialReturn={returnToReschedule}
        />
      ) : null}
    </>
  );
}
